/**
 * « Aujourd'hui », vue terrain : la suite de sa journée — interventions et
 * créneaux dans l'ordre (sauf celle de la carte « Maintenant »), puis ses
 * tâches. Les pièces réservées sont dites sur la visite à laquelle elles
 * servent. Lisible hors ligne : la journée vient alors du cache de l'appareil.
 */
import type { ReactNode } from 'react';
import { CalendarClock, Check, ListTodo, Wrench } from 'lucide-react';
import { approxClock } from '@myequip/shared';
import { RecordIcon, RecordList, RecordRow } from '../../components/RecordRow.js';
import { StateBadge } from '../../components/ui.js';
import { Section } from '../../components/ui/Section.js';
import { quantite } from '../../core/format.js';
import { t } from '../../core/i18n.js';
import type { DayIntervention, MyDay } from './home-data.js';
import type { TaskLike } from './queue.js';

/** Au plus autant de tâches d'emblée ; la page Tâches a le reste. */
const TASKS_SHOWN = 4;

function partsLine(day: MyDay, intervention: DayIntervention): string | undefined {
  const parts = day.reservations.filter((part) => part.intervention_id === intervention.id);
  if (parts.length === 0) return undefined;
  return `Pièces réservées : ${parts.map((part) => `${part.reference} × ${quantite(part.qty_milli)}`).join(', ')}`;
}

export function interventionBadge(intervention: Pick<DayIntervention, 'priority' | 'status'>): string {
  return intervention.priority === 'urgente' && intervention.status === 'planifiee' ? 'urgente' : intervention.status;
}

export function MyDayList(props: {
  day: MyDay;
  /** L'intervention déjà montrée par « Maintenant ». */
  nowId: string | undefined;
  tasks: TaskLike[];
  taskCount: number;
}): ReactNode {
  const { day } = props;
  const timeline = [
    ...day.interventions
      .filter((intervention) => intervention.id !== props.nowId)
      .map((intervention) => ({ kind: 'intervention' as const, at: intervention.starts_at, intervention })),
    ...day.events.map((event) => ({ kind: 'event' as const, at: event.starts_at, event })),
  ].sort((a, b) => a.at.localeCompare(b.at));
  const tasks = props.tasks.slice(0, TASKS_SHOWN);
  const moreTasks = props.taskCount - tasks.length;
  if (timeline.length === 0 && tasks.length === 0) return null;
  return (
    <Section id="aujourdhui" title={t('accueil.aujourdhui')}
      count={timeline.length + props.taskCount} flush>
      <RecordList label={t('accueil.aujourdhui')}>
        {timeline.map((item) => {
          if (item.kind === 'event') {
            return (
              <li key={item.event.id}>
                <RecordRow
                  className="home-day__row"
                  lead={<RecordIcon><CalendarClock size={20} /></RecordIcon>}
                  title={item.event.title || t(`planning.evenements.${item.event.kind}`)}
                  lines={[`${approxClock(item.event.starts_at, item.event.time_approx_minutes)} · ${t(`planning.evenements.${item.event.kind}`)}`]}
                />
              </li>
            );
          }
          const visit = item.intervention;
          const done = visit.status === 'cloturee';
          return (
            <li key={visit.id}>
              <RecordRow
                className="home-day__row"
                to={`/interventions/${visit.id}`}
                state={{ from: '/' }}
                lead={<RecordIcon>{done ? <Check size={20} /> : <Wrench size={20} />}</RecordIcon>}
                title={visit.customer_name || t(`interventions.types.${visit.type}`)}
                lines={[
                  `${approxClock(visit.starts_at, visit.time_approx_minutes)} · ${t(`interventions.types.${visit.type}`)}`,
                  [visit.site_label, visit.site_address].filter(Boolean).join(' · '),
                  partsLine(day, visit),
                ]}
                badge={<StateBadge state={interventionBadge(visit)} />}
              />
            </li>
          );
        })}
        {tasks.map((task) => (
          <li key={task.id}>
            <RecordRow
              className="home-day__row"
              to={`/taches?id=${task.id}`}
              lead={<RecordIcon><ListTodo size={20} /></RecordIcon>}
              title={task.title}
              lines={[task.created_by !== task.assignee_id ? `Donnée par ${task.created_by_name}` : 'Ma tâche']}
            />
          </li>
        ))}
        {moreTasks > 0 ? (
          <li>
            <RecordRow
              className="home-day__row"
              to="/taches"
              lead={<RecordIcon><ListTodo size={20} /></RecordIcon>}
              title={moreTasks === 1 ? 'Et 1 autre tâche' : `Et ${moreTasks} autres tâches`}
              lines={['Toutes mes tâches']}
            />
          </li>
        ) : null}
      </RecordList>
    </Section>
  );
}
